<template>
    <vx-expandable-card toggleable id="to" :active="active">
        <template v-slot:header>
            <div class="flex items-center pb-1 lg:pb-0">
                <span
                    class="pr-1.5 mr-2 border-y-0 border-l-0 border-r border-solid sm:pr-3 sm:mr-4"
                    :class="{
                        'border-r-transparent': !editable,
                        'border-r-slate-400': editable,
                    }"
                    >From</span
                >
                <span
                    v-if="editable"
                    data-test="summary"
                    class="text-xs font-normal text-slate-700 sm:text-base sm:font-semibold"
                >
                    {{ summary }}
                </span>
            </div>
        </template>
        <template v-if="editable && !props.success" v-slot:headerRight>
            <vx-button @click.stop="emit('update:active', true)" color="muted" size="md">
                <span class="inline-block">Edit</span>
                <font-awesome-icon
                    widthAuto
                    class="text-base hidden! sm:inline-block!"
                    :icon="faPencil"
                ></font-awesome-icon>
            </vx-button>
        </template>
        <template v-slot:content>
            <div class="flex flex-col gap-3 border bg-slate-100 border-white border-solid shadow rounded-lg p-4">
                <div v-if="groups.length" class="flex items-center gap-2">
                    <font-awesome-icon widthAuto :icon="faGrid2Plus"></font-awesome-icon>
                    <span class="text-xs font-semibold">
                        {{ pluralized('groups', groups.length, true) }}
                    </span>
                </div>

                <vx-button @click="groupsModal = true" data-test="open-groups-modal" color="muted">
                    Choose Groups
                    <font-awesome-icon widthAuto :icon="faMagnifyingGlass"></font-awesome-icon>
                </vx-button>
            </div>

            <vx-modal v-model:visible="groupsModal" size="lg" :padding="false">
                <page-select-org-unit-groups v-model="groups" :orgUnitId="props.orgUnitId">
                    <template v-slot:header>What Groups do you want to send from?</template>
                    <template v-slot:subheader>You can choose to send from 1 or more Group(s).</template>
                </page-select-org-unit-groups>

                <template v-slot:footer>
                    <div class="flex flex-col-reverse gap-4 md:flex-row">
                        <vx-button
                            @click="groupsModal = false"
                            type="button"
                            data-test="cancel-from-selection"
                            class="grow md:w-20 md:grow-0"
                            size="lg"
                            color="muted"
                        >
                            Cancel
                        </vx-button>
                        <vx-popover class="grow w-full" arrow placement="top" hover>
                            <vx-button
                                @click="groupsModal = false"
                                data-test="confirm-from-selection"
                                :disabled="groups.length === 0"
                                type="button"
                                block
                                size="lg"
                                color="primary"
                            >
                                Confirm <font-awesome-icon widthAuto :icon="faCircleCheck"></font-awesome-icon>
                            </vx-button>
                            <template v-if="groups.length === 0" v-slot:content>
                                You must select at least one Group
                            </template>
                        </vx-popover>
                    </div>
                </template>
            </vx-modal>

            <vx-button @click="confirm()" class="mt-4 lg:mt-6" size="lg" :block="true" data-test="from-continue">
                Continue
                <font-awesome-icon widthAuto class="text-base" :icon="faArrowTurnDown"></font-awesome-icon>
            </vx-button>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxExpandableCard, VxButton, VxPopover, VxModal } from '@voxie/frontend-components';
import { ref, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faPencil,
    faArrowTurnDown,
    faGrid2Plus,
    faMagnifyingGlass,
    faCircleCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { useToasted } from '~/composables/useToasted';
import { pluralized } from '../filters';
import PageSelectOrgUnitGroups from '~/components/general/PageSelectOrgUnitGroups.vue';

const toasted = useToasted();

const groupsModal = ref(false);

const active = defineModel('active', {
    type: Boolean,
    default: false,
});

const groups = defineModel({
    type: Array,
    default: () => [],
});

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    },
    success: {
        type: Boolean,
        default: false,
    },
});

const editable = computed(() => !active.value && (hasConfirmed.value || groups.value.data?.length));
const hasConfirmed = ref(false);

const summary = computed(() => {
    return pluralized('group', groups.value.length, true);
});

const confirm = () => {
    if (!groups.value.length) {
        toasted.error('You must select at least one Group');
        return;
    }
    hasConfirmed.value = true;
    emit('continue');
};

const emit = defineEmits(['continue']);
</script>
